const KSQ = (() => {
  const qsa = (selector, scope = document) => Array.from(scope.querySelectorAll(selector));
  const CART_SECTION_ID = 'cart-drawer-section';
  let cartSyncQueue = Promise.resolve();
  let lastInterceptedCartAddAt = 0;
  let lastDirectCartAddAt = 0;

  function getSelectedProductUrl() {
    const candidates = [
      document.querySelector('link[rel="canonical"][href]'),
      document.querySelector('.ksq-hero__btn[href]'),
      document.querySelector('.ksq-sticky-product[href]'),
      document.querySelector('.ksq-header__shop[href]'),
    ];

    for (const candidate of candidates) {
      const href = candidate?.getAttribute?.('href');
      if (!href || href === '#' || href.startsWith('javascript:')) continue;

      try {
        const url = new URL(href, window.location.origin);
        if (url.pathname.includes('/products/')) return url.href;
      } catch (_) {
        // Ignore malformed optional fallbacks.
      }
    }

    return document.querySelector('.ksq-header__shop[href]')?.href || '/collections/all';
  }

  function syncContinueShoppingLinks(scope = document) {
    const productUrl = getSelectedProductUrl();
    qsa('[data-ksq-continue-product]', scope).forEach((link) => {
      link.setAttribute('href', productUrl);
    });
  }

  async function fetchCartState() {
    const response = await fetch(`${window.Theme?.routes?.cart_url || '/cart'}.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store',
    });

    if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
    return response.json();
  }

  function updateCartCountUI(itemCount) {
    const count = Number(itemCount) || 0;

    qsa('.ksq-cart-count, [ref="cartBubbleCount"]').forEach((node) => {
      node.textContent = String(count);
      node.classList.toggle('hidden', count === 0);
    });

    qsa('[ref="cartBubble"]').forEach((node) => {
      node.classList.toggle('visually-hidden', count === 0);
    });
  }

  async function replaceCartDrawerSection(sectionHtml = '') {
    const selector = `#shopify-section-${CART_SECTION_ID}`;
    const currentSection = document.querySelector(selector);
    if (!currentSection) throw new Error('Current cart drawer section was not found.');

    let freshSection = null;

    // Prefer the cart section returned by Shopify's successful /cart/add response.
    // This is authoritative for the newly-created cart and avoids the first-add
    // session/cookie propagation race that can make an immediate GET look empty.
    if (typeof sectionHtml === 'string' && sectionHtml.trim()) {
      const parsed = new DOMParser().parseFromString(sectionHtml, 'text/html');
      freshSection = parsed.querySelector(selector);

      if (!freshSection) {
        const wrapper = document.createElement('div');
        wrapper.id = `shopify-section-${CART_SECTION_ID}`;
        wrapper.className = 'shopify-section';
        wrapper.innerHTML = sectionHtml;
        freshSection = wrapper;
      }
    }

    if (!freshSection) {
      const sectionUrl = new URL(window.location.href);
      sectionUrl.searchParams.set('section_id', CART_SECTION_ID);
      sectionUrl.searchParams.set('_ksq_cart_refresh', String(Date.now()));

      const response = await fetch(sectionUrl.toString(), {
        headers: { Accept: 'text/html' },
        credentials: 'same-origin',
        cache: 'no-store',
      });

      if (!response.ok) throw new Error(`Cart drawer section request failed: ${response.status}`);

      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      freshSection = parsed.querySelector(selector);

      if (!freshSection) {
        throw new Error('Fresh cart drawer section markup was not found.');
      }
    }

    const replacement = document.importNode(freshSection, true);
    currentSection.replaceWith(replacement);
    syncContinueShoppingLinks(replacement);
    return replacement;
  }

  async function openFreshCartDrawer(result = {}) {
    const detail = result?.detail || {};
    const sectionHtml = detail?.sections?.[CART_SECTION_ID] || result?.sections?.[CART_SECTION_ID] || '';
    const replacement = await replaceCartDrawerSection(sectionHtml);

    const eventCart = result?.cart;
    const renderedCount = Number(
      replacement.querySelector('[data-ksq-cart-count]')?.getAttribute('data-ksq-cart-count')
    );
    const knownCount = Number.isFinite(renderedCount)
      ? renderedCount
      : eventCart?.totalQuantity ?? detail?.itemCount;

    if (Number.isFinite(Number(knownCount))) {
      updateCartCountUI(Number(knownCount));
    } else {
      fetchCartState()
        .then((cart) => updateCartCountUI(cart.item_count ?? cart.total_quantity ?? 0))
        .catch((error) => console.warn('[ksq-cart] Cart count refresh failed:', error));
    }

    await customElements.whenDefined('theme-drawer');
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

    const drawer = document.querySelector('theme-drawer#cart-drawer');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
    }
  }

  function queueFreshCartOpen(result = {}) {
    cartSyncQueue = cartSyncQueue
      .catch(() => undefined)
      .then(() => openFreshCartDrawer(result));

    return cartSyncQueue;
  }

  function isCartAddRequest(input) {
    try {
      const rawUrl = input instanceof Request ? input.url : String(input || '');
      const url = new URL(rawUrl, window.location.origin);
      return /\/cart\/add(?:\.js)?\/?$/.test(url.pathname);
    } catch (_) {
      return false;
    }
  }

  function installCartAddFetchBridge() {
    if (window.__ksqCartFetchBridgeInstalled) return;
    window.__ksqCartFetchBridgeInstalled = true;

    const nativeFetch = window.fetch.bind(window);

    window.fetch = async function ksqCartAwareFetch(input, init) {
      const cartAddRequest = isCartAddRequest(input);
      const response = await nativeFetch(input, init);

      if (cartAddRequest && response.ok) {
        response.clone().json().then((payload) => {
          if (payload?.status) return;

          window.setTimeout(() => {
            if (Date.now() - lastDirectCartAddAt < 1500) return;

            lastInterceptedCartAddAt = Date.now();
            queueFreshCartOpen({
              sections: payload?.sections || {},
              detail: {
                sections: payload?.sections || {},
                didError: false,
              },
            }).catch((error) => {
              console.warn('[ksq-cart] Cart add response fallback failed:', error);
            });
          }, 180);
        }).catch((error) => {
          console.warn('[ksq-cart] Could not read the cart add response:', error);
          window.setTimeout(() => {
            if (Date.now() - lastDirectCartAddAt < 1500) return;

            lastInterceptedCartAddAt = Date.now();
            queueFreshCartOpen().catch((refreshError) => {
              console.warn('[ksq-cart] Fallback cart refresh/open failed:', refreshError);
            });
          }, 180);
        });
      }

      return response;
    };
  }

  function handleSuccessfulCartAdd(detail = {}) {
    lastDirectCartAddAt = Date.now();
    return queueFreshCartOpen({
      detail: {
        sections: detail.sections || {},
        itemCount: detail.itemCount,
        didError: false,
      },
    }).catch((error) => {
      console.warn('[ksq-cart] Direct product-form drawer update failed:', error);
      throw error;
    });
  }

  function initCartBridge() {
    installCartAddFetchBridge();

    if (document.documentElement.dataset.ksqCartBridgeReady === 'true') return;
    document.documentElement.dataset.ksqCartBridgeReady = 'true';

    // Horizon's standard cart event remains as a fallback. The successful
    // /cart/add response bridge above is the primary path because it contains
    // the authoritative post-add section HTML, including on the first add.
    document.addEventListener('shopify:cart:lines-update', (event) => {
      if (event.action !== 'add') return;

      const completion = event.promise && typeof event.promise.then === 'function'
        ? event.promise
        : Promise.resolve({});

      completion
        .then((result) => {
          if (result?.detail?.didError) return;
          if (Date.now() - lastDirectCartAddAt < 2500) return;
          if (Date.now() - lastInterceptedCartAddAt < 2500) return;
          return queueFreshCartOpen(result);
        })
        .catch((error) => {
          console.warn('[ksq-cart] Standard cart event fallback failed:', error);
        });
    });

    document.addEventListener('click', (event) => {
      const target = event.target instanceof Element
        ? event.target.closest('[data-ksq-continue-product]')
        : null;
      if (!target) return;

      const productUrl = getSelectedProductUrl();
      if (!productUrl) return;

      event.preventDefault();
      window.location.assign(productUrl);
    });

    syncContinueShoppingLinks();
  }

  function initMobileMenu(scope = document) {
    qsa('[data-ksq-menu-toggle]', scope).forEach((button) => {
      if (button.dataset.ksqReady) return;
      button.dataset.ksqReady = 'true';
      const id = button.getAttribute('aria-controls');
      const menu = document.getElementById(id);
      if (!menu) return;
      const close = () => {
        menu.classList.remove('is-open');
        button.setAttribute('aria-expanded', 'false');
      };
      button.addEventListener('click', () => {
        const open = menu.classList.toggle('is-open');
        button.setAttribute('aria-expanded', String(open));
      });
      menu.addEventListener('click', (event) => {
        if (event.target.closest('a')) close();
      });
      document.addEventListener('click', (event) => {
        if (!menu.classList.contains('is-open')) return;
        if (!menu.contains(event.target) && !button.contains(event.target)) close();
      });
      window.addEventListener('resize', () => {
        if (window.innerWidth >= 990) close();
      }, { passive: true });
    });
  }

  function syncBundle(groupId, option) {
    if (!groupId || !option) return;
    const variantId = option.dataset.variantId;
    const quantity = option.dataset.quantity || '1';
    const name = option.dataset.bundleName || '';
    const price = option.dataset.bundlePrice || '';
    const compare = option.dataset.bundleCompare || '';
    const available = option.dataset.available !== 'false';
    qsa(`[data-bundle-group="${CSS.escape(groupId)}"]`).forEach((group) => {
      group.dataset.selectedVariant = variantId || '';
      group.dataset.selectedQuantity = quantity;
      qsa('input[data-ksq-variant]', group).forEach((input) => { input.value = variantId || input.value; });
      qsa('input[data-ksq-quantity]', group).forEach((input) => { input.value = quantity; });
      qsa('[data-ksq-selected-name]', group).forEach((el) => { el.textContent = name; });
      qsa('[data-ksq-selected-price]', group).forEach((el) => { el.textContent = price; });
      qsa('[data-ksq-selected-compare]', group).forEach((el) => {
        el.textContent = compare;
        el.hidden = !compare;
      });
      qsa('[data-ksq-button-price]', group).forEach((el) => { el.textContent = price; });
      const productForm = group.querySelector('product-form-component');
      if (productForm) productForm.dataset.quantityDefault = quantity;
      qsa('[ref="addToCartButton"]', group).forEach((button) => { button.disabled = !available; });
    });
  }

  function initBundles(scope = document) {
    qsa('[data-ksq-bundle-options]', scope).forEach((wrapper) => {
      if (wrapper.dataset.ksqReady) return;
      wrapper.dataset.ksqReady = 'true';
      const group = wrapper.closest('[data-bundle-group]');
      const groupId = group?.dataset.bundleGroup;
      const options = qsa('[data-ksq-bundle-option]', wrapper);
      options.forEach((option) => {
        option.addEventListener('click', () => {
          options.forEach((other) => {
            const selected = other === option;
            other.classList.toggle('is-selected', selected);
            other.setAttribute('aria-pressed', String(selected));
          });
          syncBundle(groupId, option);
        });
      });
      const selected = options.find((item) => item.classList.contains('is-selected')) || options[0];
      if (selected) syncBundle(groupId, selected);
    });
  }

  function initGallery(scope = document) {
    qsa('[data-ksq-gallery]', scope).forEach((gallery) => {
      if (gallery.dataset.ksqReady) return;
      gallery.dataset.ksqReady = 'true';
      const main = gallery.querySelector('[data-ksq-main-image]');
      if (!main) return;
      qsa('[data-ksq-thumb]', gallery).forEach((thumb) => {
        thumb.addEventListener('click', () => {
          const src = thumb.dataset.src;
          const alt = thumb.dataset.alt || main.alt;
          if (!src) return;
          qsa('[data-ksq-thumb]', gallery).forEach((item) => {
            const selected = item === thumb;
            item.classList.toggle('is-selected', selected);
            item.setAttribute('aria-pressed', String(selected));
          });
          main.style.opacity = '0';
          const image = new Image();
          image.onload = () => {
            main.src = src;
            main.alt = alt;
            requestAnimationFrame(() => { main.style.opacity = '1'; });
          };
          image.src = src;
        });
      });
    });
  }

  function initSticky(scope = document) {
    qsa('[data-ksq-sticky]', scope).forEach((bar) => {
      if (bar.dataset.ksqReady) return;
      bar.dataset.ksqReady = 'true';
      const sentinelSelector = bar.dataset.sentinel;
      const sentinel = sentinelSelector ? document.querySelector(sentinelSelector) : null;
      if (!sentinel || !('IntersectionObserver' in window)) {
        bar.classList.add('is-visible');
        return;
      }
      const observer = new IntersectionObserver(([entry]) => {
        bar.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
      }, { threshold: 0.02 });
      observer.observe(sentinel);
    });
  }

  function initAddFeedback(scope = document) {
    qsa('.ksq-btn[type="submit"]', scope).forEach((button) => {
      if (button.dataset.ksqFeedbackReady) return;
      button.dataset.ksqFeedbackReady = 'true';
      const formComponent = button.closest('product-form-component');
      if (!formComponent) return;
      formComponent.addEventListener('shopify:cart:lines-update', (event) => {
        event.promise?.then(({ detail }) => {
          if (detail?.didError) return;
          const original = button.dataset.originalLabel || button.innerHTML;
          button.dataset.originalLabel = original;
          button.classList.add('is-added');
          button.innerHTML = '<span>Added to cart</span>';
          window.setTimeout(() => {
            button.innerHTML = original;
            button.classList.remove('is-added');
          }, 1200);
        }).catch(() => {});
      });
    });
  }

  function init(scope = document) {
    initCartBridge();
    syncContinueShoppingLinks(scope);
    initMobileMenu(scope);
    initBundles(scope);
    initGallery(scope);
    initSticky(scope);
    initAddFeedback(scope);
  }

  installCartAddFetchBridge();

  return { init, handleSuccessfulCartAdd };
})();

window.KSQCartAddSuccess = KSQ.handleSuccessfulCartAdd;

document.addEventListener('DOMContentLoaded', () => KSQ.init());
document.addEventListener('shopify:section:load', (event) => KSQ.init(event.target));
